import { z } from "zod";
import { fail, ok, type FieldKind, type FilterOperator } from "../kind";
import { dateTimeFormat, relativeTimeFormat } from "../intl";
import {
  dayInZone,
  dayToUtcMs,
  daysBetween,
  isIsoDay,
  isIsoInstant,
  isValidTimeZone,
  parseNaturalDate,
  zonedToEpoch,
} from "../time";
import type { CoerceCtx, FieldSettingsMap, FieldValueMap, FormatCtx } from "../types";

/**
 * `start` et `end` ont la même forme : un jour "YYYY-MM-DD" si le champ
 * n'inclut pas l'heure, un instant ISO UTC sinon.
 */
export type DateValue = { start: string; end?: string };

export const dateDisplaySettings = {
  display: z.enum(["absolute", "relative"]).default("absolute"),
  // short 23/10/2026 · medium 23 oct. 2026 · long jeudi 23 octobre 2026 ·
  // iso 2026-10-23
  dateFormat: z.enum(["short", "medium", "long", "iso"]).default("medium"),
  timeFormat: z.enum(["24h", "12h"]).default("24h"),
  // Fuseau d'affichage fixe ; absent : celui du lecteur.
  timeZone: z
    .string()
    .refine(isValidTimeZone, "Unknown time zone.")
    .optional(),
};

export const dateSettings = z.object({
  includeTime: z.boolean().default(false),
  isRange: z.boolean().default(false),
  ...dateDisplaySettings,
});

type DateDisplaySettings = {
  display: "absolute" | "relative";
  dateFormat: "short" | "medium" | "long" | "iso";
  timeFormat: "24h" | "12h";
  timeZone?: string;
};

const pointSchema = z.string().refine((s) => isIsoDay(s) || isIsoInstant(s), "Invalid date.");

/** Un point (jour ou instant) dans la forme demandée par le champ. */
function coercePoint(
  input: unknown,
  includeTime: boolean,
  ctx: CoerceCtx,
): string | null {
  let day: string;
  let epoch: number | null = null;
  if (typeof input === "number" && Number.isFinite(input)) {
    epoch = input;
    day = dayInZone(input, ctx.timeZone);
  } else if (typeof input === "string") {
    const text = input.trim();
    if (isIsoInstant(text)) {
      epoch = Date.parse(text);
      day = dayInZone(epoch, ctx.timeZone);
    } else {
      const parsed = parseNaturalDate(text, ctx);
      if (!parsed) return null;
      day = parsed.day;
      if (parsed.time) {
        epoch = zonedToEpoch(day, parsed.time.hour, parsed.time.minute, ctx.timeZone);
      }
    }
  } else {
    return null;
  }
  if (!includeTime) return day;
  return new Date(epoch ?? zonedToEpoch(day, 0, 0, ctx.timeZone)).toISOString();
}

/** « du 1er au 5 », « 2026-10-01 → 2026-10-05 », `{ start, end }`. */
function splitRange(input: unknown): { start: unknown; end?: unknown } {
  if (input && typeof input === "object" && !Array.isArray(input) && "start" in input) {
    const value = input as { start: unknown; end?: unknown };
    return { start: value.start, end: value.end ?? undefined };
  }
  if (Array.isArray(input) && input.length === 2) return { start: input[0], end: input[1] };
  if (typeof input === "string") {
    const parts = input.split(/\s*(?:→|->|–|—|\s+to\s+|\s+au\s+|\s+until\s+|\s+jusqu'au\s+)\s*/i);
    if (parts.length === 2 && parts[0] && parts[1]) return { start: parts[0], end: parts[1] };
  }
  return { start: input };
}

export function pointToEpoch(point: string): number {
  return isIsoDay(point) ? dayToUtcMs(point) : Date.parse(point);
}

/** Le jour d'un point, dans un fuseau (un jour reste lui-même). */
function pointDay(point: string, timeZone: string): string {
  return isIsoDay(point) ? point : dayInZone(Date.parse(point), timeZone);
}

const DATE_STYLES = {
  short: { day: "2-digit", month: "2-digit", year: "numeric" },
  medium: { day: "numeric", month: "short", year: "numeric" },
  long: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
} as const;

/** Un point en texte, selon les réglages d'affichage. Partagé avec created_time. */
export function formatPoint(
  point: string,
  settings: DateDisplaySettings,
  ctx: FormatCtx,
): string {
  const timeZone = settings.timeZone ?? ctx.timeZone;
  const hasTime = !isIsoDay(point);
  if (ctx.style === "plain") {
    if (!hasTime) return point;
    // Heure murale du fuseau, relisible par `parseNaturalDate`.
    const day = dayInZone(Date.parse(point), timeZone);
    const time = dateTimeFormat("en-GB", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(Date.parse(point));
    return `${day} ${time}`;
  }

  if (settings.display === "relative") {
    const rtf = relativeTimeFormat(ctx.locale, { numeric: "auto" });
    if (!hasTime) {
      const days = daysBetween(dayInZone(ctx.now, timeZone), point);
      if (Math.abs(days) < 7) return rtf.format(days, "day");
      if (Math.abs(days) < 30) return rtf.format(Math.round(days / 7), "week");
      if (Math.abs(days) < 365) return rtf.format(Math.round(days / 30), "month");
      return rtf.format(Math.round(days / 365), "year");
    }
    const seconds = (Date.parse(point) - ctx.now) / 1000;
    const abs = Math.abs(seconds);
    if (abs < 60) return rtf.format(Math.round(seconds), "second");
    if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute");
    if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), "hour");
    if (abs < 30 * 86_400) return rtf.format(Math.round(seconds / 86_400), "day");
    if (abs < 365 * 86_400) return rtf.format(Math.round(seconds / (30 * 86_400)), "month");
    return rtf.format(Math.round(seconds / (365 * 86_400)), "year");
  }

  const time = hasTime
    ? dateTimeFormat(ctx.locale, {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: settings.timeFormat === "12h",
      }).format(Date.parse(point))
    : null;
  let date: string;
  if (settings.dateFormat === "iso") {
    date = pointDay(point, timeZone);
  } else {
    // Un jour se formate en UTC : c'est une case du calendrier, pas un instant.
    date = dateTimeFormat(ctx.locale, {
      ...DATE_STYLES[settings.dateFormat],
      timeZone: hasTime ? timeZone : "UTC",
    }).format(hasTime ? Date.parse(point) : dayToUtcMs(point));
  }
  return time ? `${date} ${time}` : date;
}

/**
 * Les opérateurs de date comparent des JOURS : l'opérande est un jour
 * (« aujourd'hui » compris), et un instant est ramené à son jour dans le
 * fuseau du champ, sinon du lecteur.
 */
function dayOperator<T extends "date" | "created_time" | "updated_time">(
  id: "eq" | "before" | "after" | "on_or_before" | "on_or_after",
  startOf: (value: FieldValueMap[T]) => string,
  test: (day: string, operand: string) => boolean,
): FilterOperator<T, string> {
  return {
    id,
    operand: "day",
    test: (value, operand, settings, ctx) =>
      test(
        pointDay(startOf(value), (settings as { timeZone?: string }).timeZone ?? ctx.timeZone),
        operand,
      ),
  };
}

export function dayOperators<T extends "date" | "created_time" | "updated_time">(
  startOf: (value: FieldValueMap[T]) => string,
): FilterOperator<T, string>[] {
  return [
    dayOperator<T>("eq", startOf, (a, b) => a === b),
    dayOperator<T>("before", startOf, (a, b) => a < b),
    dayOperator<T>("after", startOf, (a, b) => a > b),
    dayOperator<T>("on_or_before", startOf, (a, b) => a <= b),
    dayOperator<T>("on_or_after", startOf, (a, b) => a >= b),
  ];
}

export const dateKind: FieldKind<"date"> = {
  type: "date",
  settings: dateSettings,
  value: z.object({ start: pointSchema, end: pointSchema.optional() }),
  coerce(input, settings: FieldSettingsMap["date"], ctx) {
    const range = splitRange(input);
    const start = coercePoint(range.start, settings.includeTime, ctx);
    if (start === null) return fail(`"${String(range.start)}" is not a date.`);
    if (range.end === undefined || range.end === null || range.end === "") {
      return ok({ start });
    }
    if (!settings.isRange) return fail("This date field does not accept a range.");
    const end = coercePoint(range.end, settings.includeTime, ctx);
    if (end === null) return fail(`"${String(range.end)}" is not a date.`);
    if (pointToEpoch(end) < pointToEpoch(start)) {
      return fail("The end date is before the start date.");
    }
    return ok({ start, end });
  },
  format: (value, settings, ctx) => {
    const start = formatPoint(value.start, settings, ctx);
    return value.end ? `${start} → ${formatPoint(value.end, settings, ctx)}` : start;
  },
  compare: (a, b) => pointToEpoch(a.start) - pointToEpoch(b.start),
  filters: dayOperators<"date">((value) => value.start),
  aggregates: [
    "count",
    "count_empty",
    "count_not_empty",
    "percent_empty",
    "percent_not_empty",
    "earliest",
    "latest",
    "range",
  ],
  toNumber: (value) => pointToEpoch(value.start),
};
